import { useCallback, useRef, useState } from "react";

import axios from "axios";

import {
  acquireCSRFToken,
  defaultApiUrl,
  API_VERSION,
} from "../../../../libraries/network.implementation";

export interface ReceiptUploadResponse {
  uuid: string;
  status: string;
  original_filename?: string;
}

export type UploadStatus = "idle" | "uploading" | "success" | "error";

interface UseReceiptUploadResult {
  status: UploadStatus;
  progress: number;
  response: ReceiptUploadResponse | null;
  upload: (file: File) => Promise<void>;
  remove: (uuid: string) => Promise<void>;
  reset: () => void;
  abort: () => void;
}

const RECEIPTS_ENDPOINT = `${defaultApiUrl}/${API_VERSION}/receipts/`;

const useReceiptUpload = (): UseReceiptUploadResult => {
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [response, setResponse] = useState<ReceiptUploadResponse | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setStatus("idle");
    setProgress(0);
    setResponse(null);
    abortControllerRef.current = null;
  }, []);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const upload = useCallback(async (file: File) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setStatus("uploading");
    setProgress(0);
    setResponse(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Django session middleware rejects unauthenticated POSTs without an
      // x-csrftoken header. The shared network wrapper handles this for
      // JSON requests, but the multipart upload bypasses it (no progress /
      // abort support), so we acquire the token directly here.
      const csrfToken = await acquireCSRFToken();
      const result = await axios.post<ReceiptUploadResponse>(
        RECEIPTS_ENDPOINT,
        formData,
        {
          signal: controller.signal,
          // Browser sets multipart boundary itself; do not hand-roll Content-Type.
          headers: { Accept: "application/json", "x-csrftoken": csrfToken },
          withCredentials: true,
          onUploadProgress: (event) => {
            const total = event.total ?? file.size;
            if (!total) return;
            const next = Math.min(
              100,
              Math.round((event.loaded / total) * 100)
            );
            setProgress(next);
          },
        }
      );
      setProgress(100);
      setResponse(result.data);
      setStatus("success");
    } catch (error) {
      if (axios.isCancel?.(error)) {
        // Abort was intentional (modal close) — leave status alone and don't
        // surface anything to the caller.
        return;
      }
      setStatus("error");
      // Re-throw so the caller can read `error.response?.data?.id` and pick
      // the right variant toast. The hook stays UI-agnostic.
      throw error;
    } finally {
      abortControllerRef.current = null;
    }
  }, []);

  const remove = useCallback(async (uuid: string) => {
    // Same CSRF concern as upload(): this path bypasses the shared network
    // wrapper, so attach the token directly.
    const csrfToken = await acquireCSRFToken();
    await axios.delete(`${RECEIPTS_ENDPOINT}${uuid}/`, {
      headers: { Accept: "application/json", "x-csrftoken": csrfToken },
      withCredentials: true,
    });
  }, []);

  return { status, progress, response, upload, remove, reset, abort };
};

export default useReceiptUpload;
